import { ChangeEvent, KeyboardEvent, ReactNode, useState } from "react";

import { Icon, TextButton, TextField } from "../";
import styled from "styled-components";
import { alpha } from "../../typography";
import { TextFieldProps } from "../TextField/TextField";

export enum EditableLabelTestIds {
  EditIcon = "editableLabel-edit-icon",
  InputField = "editableLabel-input-field",
}

const TextFieldWrapper = styled.div<{ $hasError: boolean }>`
  ${({ $hasError }) => !$hasError && "padding-bottom: 10px;"}
  padding-top: 10px;
`;

const StyledTextButton = styled(TextButton)`
  span {
    color: var(--color-secondary-800);
  }
`;

const EditFormContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: var(--size-font-small);
`;

const ChildrenWrapper = styled.span`
  ${alpha.primaryActive.body3}
  display: flex;
  align-items: center;
  .icon-edit {
    display: none;
  }
  &:hover .icon-edit {
    display: block;
    margin-left: 8px;
    height: 16px;
    width: 16px;
    cursor: pointer;
  }
`;

type EditableLabelProps = {
  value: string;
  /* When an error is thrown the label will not disappear */
  onSubmit: (value: string) => void;
  onChange?: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
  onEditBtnClick?: () => void;
};

type Props = EditableLabelProps &
  Omit<TextFieldProps, keyof EditableLabelProps>;

export const EditableLabel = ({
  value,
  onChange,
  onSubmit,
  children,
  onEditBtnClick,
  disabled,
  ...textFieldProps
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(e.target.value);
  };

  /**
   * Ignore user writing spaces in the field if the field is empty
   */
  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.code === "Space" && value.length === 0) {
      event.preventDefault();
    }
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      await onSubmit?.(value);
      setIsEditing(false);
    } catch (_e) {
      setIsEditing(true);
    }
  };

  return isEditing ? (
    <EditFormContainer>
      <TextFieldWrapper $hasError={Boolean(textFieldProps.errorMessage)}>
        <TextField
          autoFocus
          aria-label={EditableLabelTestIds.InputField}
          value={value}
          onChange={handleNameChange}
          onKeyDown={handleNameKeyDown}
          {...textFieldProps}
        />
      </TextFieldWrapper>
      <StyledTextButton onClick={handleSubmit} disabled={disabled}>
        Save
      </StyledTextButton>
    </EditFormContainer>
  ) : (
    <ChildrenWrapper>
      {children}
      {!disabled && (
        <Icon
          data-testid={EditableLabelTestIds.EditIcon}
          className="icon-edit"
          iconName="edit"
          iconSize="small"
          onClick={() => {
            setIsEditing(true);
            onEditBtnClick && onEditBtnClick();
          }}
        />
      )}
    </ChildrenWrapper>
  );
};
